import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { HealthSummaryResponse } from "@/lib/types"
import { formatDateOnly } from "@/lib/datetime"
import { FileText, CheckCircle2, ExternalLink } from "lucide-react"

interface Props {
  summaries: HealthSummaryResponse[]
  loading?: boolean
}

function monthLabel(summary_month: string) {
  // A calendar month ("2026-10-01"): never shifted by time zone.
  return formatDateOnly(summary_month, "MMMM yyyy", summary_month)
}

const ChangeGroup: React.FC<{ label: string; items?: string[] }> = ({ label, items }) =>
  items && items.length > 0 ? (
    <div className="mt-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-success">{label}</span>
      <ul className="ml-4 list-disc text-sm text-foreground">
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  ) : null

const PatientMonthlyPlansTab: React.FC<Props> = ({ summaries, loading }) => {
  // Only generated monthly care plans (have a rendered PDF); chart-receipt rows have pdf_path null.
  const monthly = (summaries || [])
    .filter((s) => !!s.pdf_path)
    .sort((a, b) => (a.summary_month < b.summary_month ? 1 : -1))

  if (loading) return <div className="p-6 text-muted-foreground">Loading…</div>
  if (monthly.length === 0)
    return <div className="p-6 text-muted-foreground">No monthly care plans yet.</div>

  return (
    <div className="space-y-4 p-2">
      {monthly.map((s) => {
        const a = s.diff_from_previous_summary?.analysis
        const mc = a?.medication_changes
        const hasChanges = !!s.diff_from_previous_summary?.has_changes
        return (
          <Card key={s.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">{monthLabel(s.summary_month)} Care Plan</CardTitle>
              <div className="flex items-center gap-2">
                {s.ecw_document_id ? (
                  <Badge variant="secondary" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> In ECW
                  </Badge>
                ) : s.approval_status === "approved" ? (
                  <Badge variant="outline">Approved</Badge>
                ) : (
                  <Badge variant="outline">Draft</Badge>
                )}
                {s.pdf_path && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={s.pdf_path} target="_blank" rel="noopener noreferrer">
                      <FileText className="mr-1 h-4 w-4" /> View PDF <ExternalLink className="ml-1 h-3 w-3" />
                    </a>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-success/30 bg-success/10 p-3">
                <div className="text-sm font-semibold text-success">What Changed Since Last Month</div>
                {!hasChanges ? (
                  <div className="mt-1 text-sm italic text-muted-foreground">
                    No clinically significant changes since last month.
                  </div>
                ) : (
                  <>
                    <ChangeGroup label="New conditions" items={a?.new_conditions_added} />
                    <ChangeGroup label="Conditions resolved" items={a?.conditions_removed} />
                    <ChangeGroup label="Medications started" items={mc?.added} />
                    <ChangeGroup label="Medications discontinued" items={mc?.discontinued} />
                    <ChangeGroup label="Dosage changes" items={mc?.dosage_changes} />
                    <ChangeGroup label="Key clinical changes" items={a?.key_clinical_changes} />
                    <ChangeGroup label="Care gap changes" items={a?.care_gap_changes} />
                    <ChangeGroup label="Risk factors" items={a?.risk_factors} />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

export default PatientMonthlyPlansTab
